import React, { useState, useEffect } from "react";
import HeaderMobile from "./HeaderMobile.js";
import Header from "./Header.js";
import Sidebar from "./Sidebar.js";
import Footer from "./Footer.js";
import { BASE_URL } from "./Constants.js";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const OverAllStockReport = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState("");
const [toDate, setToDate] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [areaFilter, setAreaFilter] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const handleToggleSidebar = () => {
    if (window.innerWidth >= 992) {
      setIsSidebarOpen((prev) => !prev);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Fetch Stock Balance Report
  useEffect(() => {
    const fetchStockReport = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        const res = await fetch(`${BASE_URL}overall_stock_balance_report`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        const data = await res.json();
        if (data.status && Array.isArray(data.data)) {
          setStockData(data.data);
        } else {
          setError("No stock data found");
        }
      } catch (err) {
        setError(err.message || "Failed to fetch stock data");
      } finally {
        setLoading(false);
      }
    };

    fetchStockReport();
  }, []);

  // Filter logic
 const filteredStock = stockData.filter((item) => {
  const searchMatch = Object.values(item)
    .join(" ")
    .toLowerCase()
    .includes(searchQuery.toLowerCase());

  const areaMatch = !areaFilter || item.area_id === areaFilter;

  // Date filter logic
  const itemDate = new Date(item.delivery_given_date);
  const fromDateMatch = fromDate ? itemDate >= new Date(fromDate) : true;
  const toDateMatch = toDate ? itemDate <= new Date(toDate) : true;

  return searchMatch && areaMatch && fromDateMatch && toDateMatch;
});

  // Pagination
  const indexOfLastStock = currentPage * itemsPerPage;
  const indexOfFirstStock = indexOfLastStock - itemsPerPage;
  const currentStock = filteredStock.slice(indexOfFirstStock, indexOfLastStock);

  const totalPages = Math.ceil(filteredStock.length / itemsPerPage);

  if (loading) return <div className="text-center mt-4">Loading...</div>;
  if (error) return <div className="text-center mt-4 text-danger">Error: {error}</div>;

  // Excel Export
  const handleExcelExport = () => {
    const exportData = filteredStock.map((item) => ({
      Area: item.area_name,
      "Dress Type": item.dress_type_name,
      Size: item.dress_size_name,
      Color: item.dress_color_name,
      Gender: item.gender,
      Received: item.received_stock,
      Issued: item.issued_stock,
      Pending: item.pending_issue,
      TotalSales: item.total_sale,
      PhysicalStock: item.physical_stock,
      BalancePurchaseReport: item.balance_purchase_report,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stock Report");
    saveAs(
      new Blob([XLSX.write(wb, { bookType: "xlsx", type: "array" })]),
      "Stock_Balance_Report.xlsx"
    );
  };

  // Unique Area List
  const areaList = [...new Map(stockData.map((item) => [item.area_id, item])).values()];

  return (
    <div className="page-wrapper">
      <HeaderMobile />
      <Sidebar isOpen={isSidebarOpen} />
      
      <div className="page-container" style={{
        marginLeft: isSidebarOpen && window.innerWidth >= 992 ? "250px" : "0",
        transition: "margin-left 0.3s ease"
      }}>
        
        <Header onToggleSidebar={handleToggleSidebar} isSidebarOpen={isSidebarOpen} />

        <div className="main-content">
          <div className="section__content section__content--p30">
            <div className="container-fluid">

              <h3 className="mb-3">Stock Balance Report</h3>

              {/* Tools Row */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                
                {/* Search */}
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search..."
                  style={{ width: "250px" }}
                  value={searchQuery}
                  onChange={handleSearchChange}
                />

                {/* Area Filter */}
                <select
                  className="form-control"
                  style={{ width: "200px" }}
                  value={areaFilter}
                  onChange={(e) => setAreaFilter(e.target.value)}
                >
                  <option value="">All Areas</option>
                  {areaList.map((area, index) => (
                    <option key={index} value={area.area_id}>
                      {area.area_name}
                    </option>
                  ))}
                </select>

                <div className="d-flex align-items-center mb-3">
  <label htmlFor="fromDate" className="mr-2">From Date:</label>
  <input
    type="date"
    id="fromDate"
    className="form-control"
    value={fromDate}
    onChange={(e) => setFromDate(e.target.value)}
  />

  <label htmlFor="toDate" className="ml-3 mr-2">To Date:</label>
  <input
    type="date"
    id="toDate"
    className="form-control"
    value={toDate}
    onChange={(e) => setToDate(e.target.value)}
  />
</div>

                {/* Items per page */}
                <select
                  className="form-select"
                  style={{ width: "100px" }}
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>

                {/* Excel Export */}
                <button className="btn btn-success" onClick={handleExcelExport}>
                  Export Excel
                </button>
              </div>

              {/* Table */}
              <div className="table-responsive">
                <table className="table table-bordered table-striped">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Area</th>
                      <th>Dress Type</th>
                      <th>Size</th>
                      <th>Color</th>
                      <th>Gender</th>
                      <th>Received</th>
                      <th>Issued</th>
                      <th>Pending</th>
                      <th>Total Sales</th>
                      <th>Physical</th>
                      <th>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentStock.length > 0 ? (
                      currentStock.map((item, index) => (
                        <tr key={index}>
                          <td>{indexOfFirstStock + index + 1}</td>
                          <td>{item.area_name}</td>
                          <td>{item.dress_type_name}</td>
                          <td>{item.dress_size_name}</td>
                          <td>{item.dress_color_name}</td>
                          <td>{item.gender}</td>
                          <td>{item.received_stock}</td>
                          <td>{item.issued_stock}</td>
                          <td>{item.pending_issue}</td>
                          <td>{item.total_sale}</td>
                          <td>{item.physical_stock}</td>
                          <td>{item.balance_purchase_report}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="11" className="text-center">
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="d-flex justify-content-center mt-3">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                  <button
                    key={pageNum}
                    className={`btn btn-sm mx-1 ${pageNum === currentPage ? "btn-primary" : "btn-light"}`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <div className="text-center mt-2">
                Page {currentPage} of {totalPages}
              </div>

            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default OverAllStockReport;
